// MuralStyle.js
// Mesma linguagem visual das outras secoes: fundo fosco, destaque coral,
// borda de 2px, raio de 10px e a pulsacao suave ja usada na Timeline.

import styled, { keyframes, css } from 'styled-components';
import { colors } from '../../styles/variables';

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.08); }
`;

const surgir = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const bater = keyframes`
  0%   { transform: scale(1); }
  35%  { transform: scale(1.35); }
  60%  { transform: scale(0.92); }
  100% { transform: scale(1); }
`;

const brilhar = keyframes`
  0%   { background-position: -180% 0; }
  100% { background-position: 180% 0; }
`;

export const MuralContainer = styled.section`
  padding: 40px 20px 60px;
  background-color: ${colors.background};
  text-align: center;
  max-width: 1100px;
  margin: 0 auto;
`;

export const SectionTitle = styled.h2`
  color: ${colors.text};
  font-size: 1.7em;
  margin-bottom: 0.5em;
`;

export const Subtitle = styled.p`
  color: ${colors.text};
  font-size: 0.9em;
  margin-bottom: 0.9em;

  span {
    color: ${colors.highlight};
  }
`;

export const InteractiveText = styled.p`
  color: ${colors.highlight};
  font-size: 0.6em;
  margin-bottom: 20px;
  animation: ${pulse} 2.1s ease-in-out infinite;
`;

/* ---------------------------------------------------------------- form */

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 540px;
  margin: 0 auto 34px;
  text-align: left;
`;

export const CampoWrapper = styled.div`
  position: relative;
`;

const campoBase = css`
  width: 100%;
  background-color: transparent;
  color: ${colors.text};
  border: 2px solid ${({ $erro }) => ($erro ? colors.highlight : 'rgba(255, 255, 255, 0.18)')};
  border-radius: 10px;
  padding: 12px 64px 12px 14px;
  font-family: inherit;
  font-size: 0.95em;
  resize: none;
  transition: border-color 0.25s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.35);
  }

  &:focus {
    outline: none;
    border-color: ${colors.highlight};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const Textarea = styled.textarea`
  ${campoBase};
  min-height: 88px;
  line-height: 1.45;
`;

export const Input = styled.input`
  ${campoBase};
  padding-left: 30px;
`;

/* O "@" fixo dentro do campo, para deixar o formato obvio sem explicar. */
export const Arroba = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.highlight};
  font-size: 0.95em;
  pointer-events: none;
`;

export const Contador = styled.span`
  position: absolute;
  right: 12px;
  bottom: 10px;
  font-size: 0.7em;
  color: ${({ $perto }) => ($perto ? colors.highlight : 'rgba(255, 255, 255, 0.35)')};
  pointer-events: none;
`;

export const Botao = styled.button`
  align-self: center;
  min-width: 190px;
  padding: 11px 26px;
  font-size: 0.9em;
  letter-spacing: 0.02em;
  transition: opacity 0.2s ease, transform 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    transform: scale(0.97);
  }
`;

export const Aviso = styled.p`
  font-size: 0.78em;
  text-align: center;
  margin-top: 2px;
  color: ${({ $tipo }) => ($tipo === 'erro' ? colors.highlight : colors.accentGreen)};
  animation: ${surgir} 0.3s ease;
`;

export const Restantes = styled.p`
  font-size: 0.68em;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
`;

/* ---------------------------------------------------------------- lista */

/**
 * Trilho horizontal, no mesmo padrao da Timeline.
 *
 * A primeira versao era uma grade vertical com scroll infinito, e isso tinha
 * um defeito serio: cada pagina carregada empurrava as Redes Sociais e o
 * rodape mais para baixo. Com algumas centenas de frases essas secoes ficavam
 * inalcancaveis. Rolando para o lado, a altura da pagina e sempre a mesma,
 * nao importa quantas frases existam.
 */
export const Trilho = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  padding-bottom: 6px;
  text-align: left;
  /* Nao sequestra a rolagem vertical da pagina no celular. */
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 14px;
  flex: 0 0 auto;
  width: 280px;
  min-height: 165px;
  padding: 18px;
  scroll-snap-align: start;
  border: 2px solid ${colors.highlight};
  border-radius: 10px;
  background-color: ${colors.background};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  animation: ${surgir} 0.35s ease both;

  /* Nao renderiza o que esta fora da tela: mural grande continua leve. */
  content-visibility: auto;
  contain-intrinsic-size: 280px 165px;

  @media (max-width: 480px) {
    width: 84vw;
  }
`;

export const Frase = styled.p`
  color: ${colors.text};
  font-size: 0.95em;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

export const Rodape = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const Handle = styled.span`
  color: ${colors.highlight};
  font-size: 0.78em;
  overflow-wrap: anywhere;
`;

export const BotaoCurtir = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 4px 2px;
  color: ${({ $curtiu }) => ($curtiu ? colors.highlight : 'rgba(255, 255, 255, 0.55)')};
  font-size: 0.78em;
  cursor: pointer;

  &:hover {
    opacity: 1;
    color: ${colors.highlight};
  }

  svg {
    width: 17px;
    height: 17px;
    ${({ $animando }) => $animando && css`animation: ${bater} 0.45s ease;`}
  }
`;

/* ------------------------------------------------------- estados vazios */

export const Skeleton = styled.div`
  flex: 0 0 auto;
  width: 280px;
  min-height: 165px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.06);
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0.03) 30%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.03) 70%
  );
  background-size: 220% 100%;
  animation: ${brilhar} 1.4s linear infinite;

  @media (max-width: 480px) {
    width: 84vw;
  }
`;

export const Vazio = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85em;
  padding: 26px 0;
`;

/** Alvo do IntersectionObserver, no fim do trilho. */
export const Sentinela = styled.div`
  flex: 0 0 1px;
  width: 1px;
`;

/** Ultimo card do trilho: fecha a lista sem parecer que travou. */
export const CardFinal = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 280px;
  min-height: 165px;
  padding: 18px;
  align-items: center;
  justify-content: center;
  text-align: center;
  border: 2px dashed rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78em;
  line-height: 1.5;
  scroll-snap-align: start;

  @media (max-width: 480px) {
    width: 84vw;
  }
`;

export const Total = styled.p`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.68em;
  padding-top: 14px;
`;
